/* eslint-disable prettier/prettier */
import { displayGratuityFeature } from 'businessLogic/Payment/Gratuity';
import HttpClient from 'server/helpers/HttpClient';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { saleSelectors, isForcedAutopayRecurringSelector } from 'store/sale/selectors';
import { insightSelectors } from 'store/insight/selectors';
import { isFrictionlessLoginEligibleSelector } from 'store/auth/selectors';
import {
  isSurchargePaintedDoorOnForSale,
  getSurchargeAllData,
  isSurchargeControlGroupForSale,
} from 'components/Shared/creditCardSurcharging/creditCardSurcharging';
import { nameValidator } from 'components/Core/WalletForms/validations/src/name';
import SegmentIO from 'reporting/SegmentIO';
function getFeatureEligibility(store = {}) {
  const { sale, insight = {}, featureFlags = {}, auth = {} } = store;
  const {
    isPayable,
    shouldShowSchedulePay,
    shouldShowAutopay,
    payorSegmentation: { hasMintAccount, hasMailchimpAccount, hasQuickbooksAccount } = {},
  } = insight;
  const isFaasWidgetEligible = insightSelectors.isValidFinancingOfferSelector(insight);
  const feature_eligibility = [];
  feature_eligibility.push(getMerchantLogoStatus(store));

  if (isPayable) {
    const isTipsEnabled = displayGratuityFeature(store);
    if (isTipsEnabled) {
      feature_eligibility.push('tips');
    }
  }
  if (shouldShowSchedulePay) {
    feature_eligibility.push('schedule_pay');
  }
  if (shouldShowAutopay) {
    feature_eligibility.push('auto_pay');
  }
  if (isForcedAutopayRecurringSelector(store)) {
    feature_eligibility.push('forced_autopay');
  }
  if (
    isFrictionlessLoginEligibleSelector({
      featureFlags,
      auth,
      sale,
      insight,
    })
  ) {
    feature_eligibility.push('isfrictionlesseligible');
  }
  if (hasMintAccount || hasMailchimpAccount || hasQuickbooksAccount) {
    const account = hasQuickbooksAccount
      ? 'qbo'
      : hasMailchimpAccount
      ? 'mailchimp'
      : 'creditkarma';
    feature_eligibility.push(`${account}_login`);
  }

  if (
    saleSelectors.achOnlineConvenienceFeeEnabledSelector(sale) &&
    !isSurchargePaintedDoorOnForSale({ sale, featureFlags })
  ) {
    const feeAmount =
      saleSelectors.achOnlineConvenienceFeeAmountPaidSelector(sale) ||
      saleSelectors.achOnlineConvenienceFeeAmountSelector(sale);
    feature_eligibility.push(`convenience_fee_amount_${feeAmount}`);
  }
  // Add all growth experiment parameters from URL
  const growthExperimentParams = getGrowthExperimentParams();
  if (growthExperimentParams.length > 0) {
    feature_eligibility.push(...growthExperimentParams);
  }
  if (isFaasWidgetEligible) {
    feature_eligibility.push('faas_widget');
  }
  if (
    isSurchargePaintedDoorOnForSale({ sale, featureFlags }) ||
    isSurchargeControlGroupForSale({ sale, featureFlags })
  ) {
    const ccSurchargeData = getSurchargeAllData({ sale, featureFlags });
    const experimentBucketName = ccSurchargeData?.cc?.experimentBucketName;
    feature_eligibility.push(experimentBucketName);
  }
  return feature_eligibility;
}

function getMerchantLogoStatus(store) {
  const { companyInfo = {} } = store;
  let merchantLogoStatus = '',
    companyLogoUrl = '';
  companyLogoUrl = companyInfoSelectors.logoSelector(companyInfo);

  merchantLogoStatus = companyLogoUrl ? 'MerchantHasLogo' : 'MerchantHasNoLogo';
  return merchantLogoStatus;
}

function getScreenPrevious() {
  const isFromSignIn = (localStorage?.getItem('from_sign_in') ?? 'false') === 'true';
  const isFromSignUp = (localStorage?.getItem('from_sign_up') ?? 'false') === 'true';

  let result = '';
  if (isFromSignIn) {
    result = 'sign_in_page';
    localStorage.removeItem('from_sign_in');
  } else if (isFromSignUp) {
    result = 'sign_up_page';
    localStorage.removeItem('from_sign_up');
  }
  return result;
}

function getLinkSource() {
  try {
    const queryString = new URLSearchParams(window?.location?.search);
    const utm = queryString.get('utm_source');
    // Normalize cta to lowercase for case-insensitive matching
    const cta = queryString.get('cta')?.toLowerCase();

    // All keys are lowercase since cta is normalized to lowercase above
    const ctaOptionsMap = {
      viewinvoicenow: 'email',
      ivrjvaibvprabj: 'email', // ROT13 encoded: viewinvoicenow
      ivfjvaibvdfabj: 'email', // Malformed ROT13 variant observed in Splunk logs
      v3invoicelink: 'v3invoicelink',
      i3vaibvpryvax: 'v3invoicelink', // ROT13 encoded: v3invoicelink
      i6vaibvdfyvax: 'v3invoicelink', // Malformed ROT13 variant observed in Splunk logs
      invoice_link: 'invoice_link', // Alternative invoice link CTA
      pdfbutton: 'pdfbutton',
      cqsohggba: 'pdfbutton', // ROT13 encoded: pdfbutton
      ceachggba: 'pdfbutton', // Malformed ROT13 variant observed in Splunk logs
      paylinkbuybutton: 'paylinkbuybutton',
      mcnunipaymentlink: 'mcnunipaymentlink',
      printpreviewpaybutton: 'legacypdfbutton',
      cevagcerivrjcnlohggba: 'legacypdfbutton', // ROT13 encoded: printpreviewpaybutton
      cevagcefivfjcblchggba: 'legacypdfbutton', // Malformed ROT13 variant observed in Splunk logs
      printpreviewqrcode: 'pdfqrcode',
      email_receipt: 'email_receipt',
      email_saved: 'email_saved', // Email saved CTA
      success_screen: 'success_screen',
      fully_paid_screen: 'fully_paid_screen',
      affirm_getstarted: 'affirm_getstarted',
      whatsappweb: 'whatsappweb',
      whatsapprethink: 'whatsapprethink', // WhatsApp rethink CTA
      sharelinkprint: 'sharelinkprint',
      fubefyvaxcevag: 'sharelinkprint', // ROT13 corrupted: sharelinkprint concatenated
      saveandcopylink: 'saveandcopylink',
      saveandsharelink: 'saveandsharelink', // Save and share link CTA
      copylistpaylink: 'copylistpaylink',
      copylistmultilink: 'copylistmultilink',
      invoicelist: 'invoicelist', // Invoice list CTA
      gtkm: 'gtkm',
      html: 'html',
      text: 'text',
      batchpayment: 'batchpayment',
    };

    const utmOptionsMap = {
      sms: 'sms',
      mms: 'mms', // MMS source
      sharelink: 'sharelink',
      funeryvax: 'sharelink', // ROT13 encoded: sharelink
      fubefyvax: 'sharelink', // Malformed ROT13 variant observed in Splunk logs
      'fubefyvax)Cyfbff': 'sharelink', // Corrupted ROT13 sharelink with extra chars
      sharelinkrp: 'sharelinkrp',
      sharelinkestimate: 'sharelinkestimate',
      mailchimp: 'mailchimp',
      hs_email: 'hs_email', // HubSpot email source
      ig: 'ig', // Instagram source
      qrcode: 'qrcode',
    };

    if (cta in ctaOptionsMap) {
      return ctaOptionsMap[cta];
    }
    if (utm in utmOptionsMap) {
      return utmOptionsMap[utm];
    }

    return null;
  } catch (e) {
    return null;
  }
}

/**
 * Map from link_source value (Amplitude) to the query param to add when building payment page URLs.
 * Use so that when users land from this flow they get the correct link_source in analytics.
 *
 * Upstream (e.g. email templates, share-link API, PDF links) should add:
 * - Email: cta=viewinvoicenow
 * - Share link: utm_source=sharelink
 * - SMS: utm_source=sms
 * - PDF button: cta=pdfbutton or cta=printPreviewPayButton (legacy)
 * See getLinkSource() ctaOptionsMap/utmOptionsMap for full list.
 */
// All cta values are lowercase since getLinkSource() normalizes cta to lowercase
const LINK_SOURCE_TO_QUERY_PARAM = {
  email: { param: 'cta', value: 'viewinvoicenow' },
  sharelink: { param: 'utm_source', value: 'sharelink' },
  sharelinkrp: { param: 'utm_source', value: 'sharelinkrp' },
  sharelinkestimate: { param: 'utm_source', value: 'sharelinkestimate' },
  sms: { param: 'utm_source', value: 'sms' },
  mailchimp: { param: 'utm_source', value: 'mailchimp' },
  qrcode: { param: 'utm_source', value: 'qrcode' },
  pdfbutton: { param: 'cta', value: 'pdfbutton' },
  legacypdfbutton: { param: 'cta', value: 'printpreviewpaybutton' },
  saveandcopylink: { param: 'cta', value: 'saveandcopylink' },
  batchpayment: { param: 'cta', value: 'batchpayment' },
};

/**
 * Appends the appropriate cta or utm_source query param to a payment page URL so that
 * link_source is set correctly in Amplitude when the user lands on the page.
 * @param {string} urlString - Full URL to the payment/invoice page
 * @param {string} linkSource - One of: 'email' | 'sharelink' | 'sharelinkrp' | 'sharelinkestimate' | 'sms' | 'mailchimp' | 'qrcode' | 'pdfbutton' | 'legacypdfbutton' | 'saveandcopylink' | 'batchpayment'
 * @returns {string} URL with param appended (or original URL if linkSource not recognized)
 */
function appendLinkSourceParam(urlString, linkSource) {
  const mapping = LINK_SOURCE_TO_QUERY_PARAM[linkSource];
  if (!mapping || !urlString) return urlString;
  try {
    const url = new URL(urlString);
    url.searchParams.set(mapping.param, mapping.value);
    return url.toString();
  } catch (e) {
    return urlString;
  }
}

function getGrowthExperimentParams() {
  try {
    const queryString = new URLSearchParams(window?.location?.search);
    // Use getAll() to get ALL 'grw' parameters, not just the first one
    const grwParams = queryString.getAll('grw');

    const growthExperimentsOptionsMap = {
      // key-values for growth-experiments
      // 'due date' experiment:
      ddet_control: 'ddet_control',
      ddet_t1: 'ddet_t1',
      ddet_t2: 'ddet_t2',
      // 'pre header' experiment:
      ddph_control: 'ddph_control',
      ddph_t1: 'ddph_t1',
      ddph_t2: 'ddph_t2',
      // 'legacy to rethink template' experiment:
      ltr_t1: 'ltr_t1',
      ltr_control: 'ltr_control',
      // 'PE Statements' experiment:
      stmts_pe: 'stmts_pe',
      stmts_pe_email: 'stmts_pe_email',
      // 'Pay button on PDF for MAIP' experiment:
      pay_maip_pdf: 'pay_maip_pdf',
      // 'Pay button on PDF for PE' experiment:
      pay_pe_pdf_control: 'pay_pe_pdf_control',
      pay_pe_pdf_t1: 'pay_pe_pdf_t1',
      // 'Change default pay button in invoice email templates' experiment:
      email_pay_button_control: 'email_pay_button_control',
      email_pay_button_t1: 'email_pay_button_t1',
      email_pay_button_t2: 'email_pay_button_t2',
      // add a second pay-button in the bottom, to invoice email templates experiment:
      second_pb_treatment: 'second_pb_treatment',
      // add qr code to invoice email templates experiment:
      qr_code_treatment: 'qr_code_treatment',
      // add invoice reminder agent experiment:
      'invoice-voice-reminder-agent': 'invoice-voice-reminder-agent',
      // 'Statements pay all' experiment:
      stmts_payall_t1: 'stmts_payall_t1',
      stmts_payall_t1_qr: 'stmts_payall_t1_qr',
    };

    // Filter and map valid parameters - trim first to use consistently
    const validParams = grwParams
      .map((param) => param?.trim())
      .filter((param) => param && param in growthExperimentsOptionsMap)
      .map((param) => growthExperimentsOptionsMap[param]);

    // Return unique values only, limited to first 5 to protect against malicious activities
    const uniqueParams = [...new Set(validParams)];
    return uniqueParams.slice(0, 5);
  } catch (e) {
    return [];
  }
}

function getGrowthExperimentUtm() {
  // Legacy function - returns first param or null for backward compatibility
  const params = getGrowthExperimentParams();
  return params.length > 0 ? params[0] : null;
}

async function isAdBlockEnabled() {
  const handleAdBlockCheckHttpError = () => {
    isAdBlockEnabled = true;
  };
  let isAdBlockEnabled = false;
  const googleAdUrl = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';

  try {
    await HttpClient.getInstance()
      .get(googleAdUrl)
      .catch((e) => {
        handleAdBlockCheckHttpError(e);
      });
  } catch (e) {
    handleAdBlockCheckHttpError(e);
  }
  return isAdBlockEnabled;
}
export const reportNameFieldValidated = (
  fieldName,
  value,
  isInNameValidationExperiment,
  source
) => {
  if (fieldName !== 'name') {
    return;
  }
  const [noError] = isInNameValidationExperiment ? [true] : nameValidator(value);
  const hasError = !noError;
  const status = hasError ? 'error' : 'valid';
  let uiObjectDetail = '';
  switch (source) {
    case 'bank':
      uiObjectDetail = 'account_holders_name';
      break;
    case 'card':
      uiObjectDetail = 'name_on_card';
      break;
  }
  if (!uiObjectDetail) {
    return;
  }
  SegmentIO.componentEngaged({
    ui_object: 'form_field',
    ui_object_detail: uiObjectDetail,
    ui_action: 'typed',
    status: status,
  });
};
export {
  getFeatureEligibility,
  getScreenPrevious,
  getMerchantLogoStatus,
  isAdBlockEnabled,
  getLinkSource,
  appendLinkSourceParam,
  getGrowthExperimentUtm,
  getGrowthExperimentParams,
};
